import api from '../client'
import { assertSessionGeneration, beginAuthSession, logoutAuthSession, queueAuthMutation, refreshAuthSession } from '../authSession'

export const authApi = {
  login: async (email, password, totpCode, authFlowId) => {
    const payload = { email, password }
    if (totpCode) {
      payload.totp_code = totpCode
    }
    if (authFlowId) {
      payload.auth_flow_id = authFlowId
    }

    const session = beginAuthSession()
    return queueAuthMutation(session.generation, async () => {
      const response = await api.post('/auth/login', payload, {
        _skipAuthRefresh: true, _authMutation: true, _authGeneration: session.generation,
      })
      if (response.data?.access_token) session.installToken(response.data.access_token)
      assertSessionGeneration(session.generation)
      return response.data
    })
  },

  startMFAEnrollment: async (authFlowId) => {
    const response = await api.post('/auth/mfa/enroll', { auth_flow_id: authFlowId }, { _skipAuthRefresh: true })
    return response.data
  },

  verifyMFAEnrollment: async (authFlowId, code) => {
    const session = beginAuthSession()
    return queueAuthMutation(session.generation, async () => {
      const response = await api.post('/auth/mfa/enroll/verify', { auth_flow_id: authFlowId, code }, {
        _skipAuthRefresh: true, _authMutation: true, _authGeneration: session.generation,
      })
      if (response.data?.access_token) session.installToken(response.data.access_token)
      assertSessionGeneration(session.generation)
      return response.data
    })
  },

  refresh: () => refreshAuthSession(async (generation) => {
    const response = await api.post('/auth/refresh', undefined, {
      _skipAuthRefresh: true, _authMutation: true, _authGeneration: generation,
    })
    return response.data
  }),

  logout: () => logoutAuthSession(async (token, rememberToken) => {
    const revoke = (accessToken) => api.post('/auth/logout', undefined, {
      _skipAuthRefresh: true, _authMutation: true, _capturedAuthToken: accessToken,
    })
    try {
      await revoke(token)
    } catch (error) {
      if (error.response?.status !== 401) throw error
      // The captured access token can have expired while its refresh cookie is
      // still live. Renew only to revoke, inside this mutation's queue position;
      // entering the shared refresh queue here would deadlock behind logout.
      const response = await api.post('/auth/refresh', undefined, {
        _skipAuthRefresh: true, _authMutation: true, _capturedAuthToken: null,
      })
      const successor = response.data?.access_token
      if (!successor) throw new Error('Refresh response has no access token')
      rememberToken(successor)
      await revoke(successor)
    }
  }),

  getProfile: async () => {
    const response = await api.get('/auth/profile')
    return response.data
  },
  
  setupMFA: async (enable) => {
    const response = await api.post('/auth/mfa/setup', { enable })
    return response.data
  },
  
  verifyMFA: async (code) => {
    const response = await api.post('/auth/mfa/verify', { code })
    return response.data
  },
  
  updateProfile: async (updates) => {
    const response = await api.patch('/auth/profile', updates)
    return response.data
  },
  
  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/password', {
      current_password: currentPassword,
      new_password: newPassword
    })
    return response.data
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/password/forgot', { email })
    return response.data
  },

  resetPassword: async (userId, code, newPassword) => {
    const response = await api.post('/auth/password/reset', {
      user_id: userId,
      code,
      new_password: newPassword
    })
    return response.data
  },

  acceptInvitation: async (userId, code, newPassword) => {
    const response = await api.post('/auth/invitations/accept', {
      user_id: userId,
      code,
      new_password: newPassword
    })
    return response.data
  },
  
  disableMFA: async (code) => {
    const response = await api.post('/auth/mfa/disable', { code })
    return response.data
  }
}
